import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — legaldecoder" },
      {
        name: "description",
        content:
          "How legaldecoder collects, uses, and protects your personal data. Your contracts are never stored or used for AI training.",
      },
      { property: "og:title", content: "Privacy Policy — legaldecoder" },
      {
        property: "og:description",
        content:
          "Zero data retention. Your contracts are analyzed in-memory and never used to train AI.",
      },
      { property: "og:url", content: "https://legaldecoder.io/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://legaldecoder.io/privacy" }],
  }),
  component: Privacy,
});

function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: April 2026</p>

        <div className="mt-10 rounded-2xl border-2 border-success/30 bg-success/5 p-6">
          <h2 className="text-xl font-bold text-success">🔒 Our No-Log Promise</h2>
          <p className="mt-3 text-foreground">
            Your contract is analysed in-memory and <strong>never stored on our servers</strong>. We
            don't log, save, archive, or use your documents to train AI models. When the analysis
            completes, your text is destroyed.
          </p>
        </div>

        <div className="prose prose-slate mt-8 max-w-none space-y-8 text-foreground">
          <section>
            <h2 className="text-xl font-semibold">1. Who we are</h2>
            <p className="mt-2 text-muted-foreground">
              This Privacy Notice explains how <strong>legaldecoder</strong> ("legaldecoder", "we",
              "us", "our") handles personal data when you use the website at legaldecoder.io and the
              legaldecoder service (the "Service"). For the personal data described in this notice,
              legaldecoder acts as the <strong>data controller</strong>.
            </p>
            <p className="mt-2 text-muted-foreground">
              Contact for privacy questions:{" "}
              <a className="underline" href="mailto:privacy@legaldecoder.io">
                privacy@legaldecoder.io
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">2. Personal data we collect and why</h2>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-sm text-muted-foreground">
                <thead className="text-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 text-left">Category</th>
                    <th className="py-2 text-left">Examples</th>
                    <th className="py-2 text-left">Purpose</th>
                    <th className="py-2 text-left">Legal basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-2">Account data</td>
                    <td className="py-2">
                      Email, display name, hashed password, OAuth identifiers
                    </td>
                    <td className="py-2">Create and secure your account; provide the Service</td>
                    <td className="py-2">Contract performance</td>
                  </tr>
                  <tr>
                    <td className="py-2">Usage &amp; telemetry</td>
                    <td className="py-2">
                      Pages visited, feature events, device type, browser, IP address, approximate
                      location
                    </td>
                    <td className="py-2">
                      Operate, secure, and improve the Service; prevent fraud and abuse
                    </td>
                    <td className="py-2">Legitimate interests</td>
                  </tr>
                  <tr>
                    <td className="py-2">Analysis metadata</td>
                    <td className="py-2">
                      Number of analyses, document type, risk level (no document content)
                    </td>
                    <td className="py-2">Enforce plan quotas and improve product features</td>
                    <td className="py-2">Contract performance / legitimate interests</td>
                  </tr>
                  <tr>
                    <td className="py-2">Support messages</td>
                    <td className="py-2">Contents of emails or support requests you send us</td>
                    <td className="py-2">Respond to your inquiries</td>
                    <td className="py-2">Legitimate interests</td>
                  </tr>
                  <tr>
                    <td className="py-2">Billing reference data</td>
                    <td className="py-2">
                      Subscription status, plan, Paddle customer/subscription IDs
                    </td>
                    <td className="py-2">Provision paid features and manage your subscription</td>
                    <td className="py-2">Contract performance / legal obligation</td>
                  </tr>
                  <tr>
                    <td className="py-2">Marketing preferences</td>
                    <td className="py-2">Email opt-in/opt-out status</td>
                    <td className="py-2">Send product updates if you opt in</td>
                    <td className="py-2">Consent</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              <strong>Document content.</strong> The text of contracts you submit is processed only
              in-memory to generate your analysis and is not retained by us after the request
              completes. We do not use your documents to train AI models.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">3. AI processing</h2>
            <p className="mt-2 text-muted-foreground">
              To generate analyses, we send the relevant document text to third-party AI providers
              (such as OpenAI and Google) under data-processing terms that prohibit training on our
              customer content. The provider processes the text to return the analysis and purges it
              from its systems within their stated retention windows (typically up to 30 days for
              abuse monitoring). We never share account identifiers or other personal data with the
              AI provider beyond what is needed to fulfil the request.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">4. Who we share data with</h2>
            <ul className="mt-2 list-disc space-y-2 pl-6 text-muted-foreground">
              <li>
                <strong>Paddle.com Market Limited</strong> — our Merchant of Record and payment
                processor. When you purchase a subscription, Paddle collects and processes your
                payment details, billing address, and tax information directly. Paddle acts as an
                independent controller for that data. See Paddle's privacy notice at{" "}
                <a
                  className="underline"
                  href="https://www.paddle.com/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  paddle.com/legal/privacy
                </a>
                .
              </li>
              <li>
                <strong>Cloud and infrastructure providers</strong> (e.g. our hosting, database, and
                email-delivery vendors) acting as processors on our behalf to run the Service.
              </li>
              <li>
                <strong>AI providers</strong> (e.g. OpenAI, Google) acting as processors that
                generate the analysis output, under contracts that prohibit training on customer
                content.
              </li>
              <li>
                <strong>Analytics and security tooling</strong> used to detect abuse and improve the
                product, configured to minimise personal data.
              </li>
              <li>
                <strong>Professional advisers</strong> (legal, accounting) where reasonably needed.
              </li>
              <li>
                <strong>Authorities</strong> where required by law, court order, or to protect our
                rights and the safety of our users.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">5. International transfers</h2>
            <p className="mt-2 text-muted-foreground">
              Our providers may process personal data outside your country, including in the United
              States. Where data is transferred from the UK or the EEA, we rely on appropriate
              safeguards such as the European Commission's Standard Contractual Clauses, the UK
              International Data Transfer Addendum, or applicable adequacy decisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">6. Data retention</h2>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-muted-foreground">
              <li>
                <strong>Document text:</strong> not retained — processed in memory only.
              </li>
              <li>
                <strong>Account data:</strong> retained while your account is active and for up to
                12 months after deletion or prolonged inactivity, then deleted or anonymised.
              </li>
              <li>
                <strong>Analysis metadata (no content):</strong> retained for up to 24 months for
                quota enforcement and product analytics.
              </li>
              <li>
                <strong>Billing and tax records:</strong> retained for the period required by tax
                and accounting law (typically 6–10 years), held by Paddle as MoR and by us in
                summary form.
              </li>
              <li>
                <strong>Support messages:</strong> retained for up to 24 months after the issue is
                resolved.
              </li>
              <li>
                <strong>Server and security logs:</strong> retained for up to 90 days.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold">7. Your rights</h2>
            <p className="mt-2 text-muted-foreground">
              Depending on where you live, you may have the right to:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-muted-foreground">
              <li>access the personal data we hold about you;</li>
              <li>request correction of inaccurate data;</li>
              <li>request deletion of your data (right to erasure);</li>
              <li>request restriction of, or object to, certain processing;</li>
              <li>request a portable copy of data you provided to us;</li>
              <li>
                withdraw consent where we rely on it (without affecting prior processing); and
              </li>
              <li>lodge a complaint with your local data-protection authority.</li>
            </ul>
            <p className="mt-2 text-muted-foreground">
              To exercise any of these rights, email{" "}
              <a className="underline" href="mailto:privacy@legaldecoder.io">
                privacy@legaldecoder.io
              </a>
              . We respond within one month and may need to verify your identity. Payment-data
              requests should be directed to Paddle at{" "}
              <a className="underline" href="https://paddle.net" target="_blank" rel="noreferrer">
                paddle.net
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">8. Security</h2>
            <p className="mt-2 text-muted-foreground">
              We implement appropriate technical and organisational measures to protect personal
              data, including TLS encryption in transit, encryption at rest for stored data,
              role-based access controls, secrets management, principle-of-least-privilege access
              for personnel, audit logging, and regular review of subprocessors. No system is
              perfectly secure; if you believe your account has been compromised, contact us
              immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">9. Cookies and similar technologies</h2>
            <p className="mt-2 text-muted-foreground">
              We use a small number of strictly necessary cookies for authentication, session
              management, and security. We may also use privacy-friendly analytics to understand
              aggregate usage. Where required by law, we ask for your consent to non-essential
              cookies and provide controls to manage your preferences. You can also clear cookies
              through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">10. Children</h2>
            <p className="mt-2 text-muted-foreground">
              The Service is not directed to children under 16 and we do not knowingly collect
              personal data from them. If you believe a child has provided us personal data, contact
              us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold">11. Changes to this notice</h2>
            <p className="mt-2 text-muted-foreground">
              We may update this notice from time to time. The "Last updated" date at the top
              reflects the most recent version. Material changes will be notified by email or
              in-app.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
